import axios from "axios";
import Result from "../../deal-builder/utils/result.utils";

// @ts-ignore
const DATABASE_API_URL = process.env.REACT_APP_API_URL;

export type OrganizationType = "Bank" | "Municipal Advisor" | "Developer";

export const ORGANIZATION_TYPES: OrganizationType[] = ["Bank", "Municipal Advisor", "Developer"];

export interface Organization {
    id: string;
    name: string;
    expiration: string;
    has_national_private_placement: boolean;
    type: OrganizationType;
}

export interface OrganizationInput {
    name: string;
    expiration: string;
    has_national_private_placement: boolean;
    type: OrganizationType;
}

export interface Team {
    id: string;
    name: string;
    organization_id: string;
}

export interface TeamInput {
    name: string;
    organization_id: string;
}

export interface AdminUser {
    id: string;
    email: string | null;
    first_name: string;
    last_name: string;
    is_admin: boolean;
    can_extract_pdf: boolean;
    blocked: boolean;
    organization_id: string;
    team_id: string | null;
}

export interface UpdateUserInput {
    first_name: string;
    last_name: string;
    is_admin: boolean;
    can_extract_pdf: boolean;
    organization_id: string;
    team_id: string | null;
}

export interface CreateUserInput {
    email: string;
    password: string;
    first_name: string;
    last_name: string;
    is_admin: boolean;
    can_extract_pdf: boolean;
    organization_id: string;
    team_id: string | null;
}

// Surfaces the server's error message (e.g. on 409 conflicts) when available
function errorMessage(e: any, fallback: string): string {
    return e?.response?.data?.error ?? fallback;
}

function authConfig(token: string) {
    return { headers: { Authorization: `Bearer ${token}` } };
}

export default class AdminAPI {

    // Organizations

    public static async getOrganizations(token: string): Promise<Result<Organization[]>> {
        try {
            const { data } = await axios.get(DATABASE_API_URL + "/v3/admin/organizations", authConfig(token));
            return { valid: true, body: data };
        } catch (e) {
            return { valid: false, error: errorMessage(e, "We couldn't fetch the organizations.") };
        }
    }

    public static async createOrganization(token: string, organization: OrganizationInput): Promise<Result<Organization>> {
        try {
            const { data } = await axios.post(DATABASE_API_URL + "/v3/admin/organizations", organization, authConfig(token));
            return { valid: true, body: data };
        } catch (e) {
            return { valid: false, error: errorMessage(e, "We couldn't create the organization.") };
        }
    }

    public static async updateOrganization(token: string, organizationId: string, organization: OrganizationInput): Promise<Result<Organization>> {
        try {
            const { data } = await axios.put(DATABASE_API_URL + "/v3/admin/organizations/" + organizationId, organization, authConfig(token));
            return { valid: true, body: data };
        } catch (e) {
            return { valid: false, error: errorMessage(e, "We couldn't update the organization.") };
        }
    }

    public static async deleteOrganization(token: string, organizationId: string): Promise<Result<undefined>> {
        try {
            await axios.delete(DATABASE_API_URL + "/v3/admin/organizations/" + organizationId, authConfig(token));
            return { valid: true };
        } catch (e) {
            return { valid: false, error: errorMessage(e, "We couldn't delete the organization.") };
        }
    }

    // Teams

    public static async getTeams(token: string): Promise<Result<Team[]>> {
        try {
            const { data } = await axios.get(DATABASE_API_URL + "/v3/admin/teams", authConfig(token));
            return { valid: true, body: data };
        } catch (e) {
            return { valid: false, error: errorMessage(e, "We couldn't fetch the teams.") };
        }
    }

    public static async createTeam(token: string, team: TeamInput): Promise<Result<Team>> {
        try {
            const { data } = await axios.post(DATABASE_API_URL + "/v3/admin/teams", team, authConfig(token));
            return { valid: true, body: data };
        } catch (e) {
            return { valid: false, error: errorMessage(e, "We couldn't create the team.") };
        }
    }

    public static async updateTeam(token: string, teamId: string, team: TeamInput): Promise<Result<Team>> {
        try {
            const { data } = await axios.put(DATABASE_API_URL + "/v3/admin/teams/" + teamId, team, authConfig(token));
            return { valid: true, body: data };
        } catch (e) {
            return { valid: false, error: errorMessage(e, "We couldn't update the team.") };
        }
    }

    public static async deleteTeam(token: string, teamId: string): Promise<Result<undefined>> {
        try {
            await axios.delete(DATABASE_API_URL + "/v3/admin/teams/" + teamId, authConfig(token));
            return { valid: true };
        } catch (e) {
            return { valid: false, error: errorMessage(e, "We couldn't delete the team.") };
        }
    }

    // Users

    public static async getUsers(token: string): Promise<Result<AdminUser[]>> {
        try {
            const { data } = await axios.get(DATABASE_API_URL + "/v3/admin/users", authConfig(token));
            return { valid: true, body: data };
        } catch (e) {
            return { valid: false, error: errorMessage(e, "We couldn't fetch the users.") };
        }
    }

    public static async createUser(token: string, user: CreateUserInput): Promise<Result<AdminUser>> {
        try {
            const { data } = await axios.post(DATABASE_API_URL + "/v3/admin/users", user, authConfig(token));
            return { valid: true, body: data };
        } catch (e) {
            return { valid: false, error: errorMessage(e, "We couldn't create the user.") };
        }
    }

    public static async updateUser(token: string, userId: string, user: UpdateUserInput): Promise<Result<AdminUser>> {
        try {
            const { data } = await axios.put(DATABASE_API_URL + "/v3/admin/users/" + encodeURIComponent(userId), user, authConfig(token));
            return { valid: true, body: data };
        } catch (e) {
            return { valid: false, error: errorMessage(e, "We couldn't update the user.") };
        }
    }

    public static async setUserBlocked(token: string, userId: string, blocked: boolean): Promise<Result<AdminUser>> {
        const action = blocked ? "block" : "unblock";
        try {
            const { data } = await axios.post(DATABASE_API_URL + "/v3/admin/users/" + encodeURIComponent(userId) + "/" + action, {}, authConfig(token));
            return { valid: true, body: data };
        } catch (e) {
            return { valid: false, error: errorMessage(e, `We couldn't ${action} the user.`) };
        }
    }

}
